// REMIX HMR BEGIN
import * as __hmr__ from "remix:hmr";
if (import.meta) {
import.meta.hot = __hmr__.createHotContext(
//@ts-expect-error
"app/hooks/useSessionRefresh.ts"
);
import.meta.hot.lastModified = "1793728204522.7678";
}
// REMIX HMR END

import { useNavigate, useRevalidator } from '@remix-run/react'
import { useEffect, useState } from 'react'

const REFRESH_LEAD_MS = 10 * 60 * 1000
const MAX_JITTER_MS = 60 * 1000
const MIN_DELAY_MS = 30 * 1000

type RefreshResponse = { expires: string | null }

export function useSessionRefresh(initialExpires: string | undefined) {
	const navigate = useNavigate()
	const { revalidate } = useRevalidator()
	const [expires, setExpires] = useState(initialExpires)

	useEffect(() => {
		setExpires(initialExpires)
	}, [initialExpires])

	useEffect(() => {
		if (!expires) return

		let cancelled = false
		let inFlight = false

		async function refresh() {
			if (inFlight) return
			inFlight = true
			try {
				const response = await fetch('/session/refresh', { method: 'POST' })
				if (cancelled) return
				if (response.status === 401) {
					navigate('/logout')
					return
				}
				if (!response.ok) return
				const data = (await response.json()) as RefreshResponse
				if (data.expires && data.expires !== expires) {
					setExpires(data.expires)
					revalidate()
				}
			} catch (error) {
				console.error('Session refresh failed:', error)
			} finally {
				inFlight = false
			}
		}

		const refreshAt =
			new Date(expires).getTime() -
			REFRESH_LEAD_MS +
			Math.random() * MAX_JITTER_MS
		const timer = setTimeout(
			refresh,
			Math.max(refreshAt - Date.now(), MIN_DELAY_MS),
		)

		function handleVisibilityChange() {
			if (document.visibilityState === 'visible' && Date.now() >= refreshAt) {
				refresh()
			}
		}
		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			cancelled = true
			clearTimeout(timer)
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [expires, navigate, revalidate])
}
